import { t } from 'i18next';

import { DialogActions } from '../actions/DialogActions';
import { UIHelper } from './UIHelper';
import { ArticleStore, ArticleItem } from '../stores/ArticleStore';
import { PriceStore } from '../stores/PriceStore';
import { ArticleListMaker } from './ArticleListMaker';
import { ProjectSettingsStore } from '../stores/ProjectSettingsStore';
import { ScreenshotHelper } from './ScreenshotHelper';
import { RELEASE_VERSION } from '../App';
import { DownloadImageSize } from '../constants/LayoutConstants';

import utils = egr.wcf.utils;
import { GlobalHelper } from './GlobalHelper';

declare global {
    interface Window {
        pdfMake: any;
    }
}

export abstract class PDFMaker {
    private static AjaxBundleConfig: JQueryAjaxSettings = {
        url: 'scripts/pdfmake.arial.bundle.js' + (utils.string.isNotNullOrEmpty(RELEASE_VERSION) ? '?v=' + RELEASE_VERSION : ''),
        dataType: 'script',
        cache: true
    };

    /**
     * Note: this function does not trigger or wait for the update of the
     * property list. Use `DownloadHelper.downloadPDF` if the content has
     * to be synchronized first.
     */
    public static async downloadPDF(): Promise<void> {
        const doc: any = await PDFMaker.generatePDF();
        doc.download(PDFMaker.getFileName());
    }

    public static generatePDF(): Promise<any> {
        return PDFMaker.loadLib().then(PDFMaker.generatePDFDocument);
    }

    public static generatePDFAsBase64(): Promise<string> {
        return PDFMaker.loadLib().then(() => {
            return new Promise(async (resolve: (buffer: string) => void, reject: (error: Error) => void) => {
                PDFMaker.generatePDFDocument().then((pdf) => {
                    pdf.getBase64(resolve);
                });
            });
        });
    }

    public static async generatePDFAsBuffer(arraybuffer: false): Promise<Uint8Array>;
    public static async generatePDFAsBuffer(arraybuffer: true): Promise<ArrayBuffer>;
    public static async generatePDFAsBuffer(arraybuffer: boolean): Promise<ArrayBuffer | Uint8Array> {
        await PDFMaker.loadLib();
        const pdf: any =  await PDFMaker.generatePDFDocument();
        const array: Uint8Array = await new Promise<Uint8Array>((resolve) => {
            pdf.getBuffer(resolve, {binary: true});
        });

        if (arraybuffer) {
            return array.buffer.slice(array.byteOffset, array.byteLength + array.byteOffset);
        }

        return array;
    }

    public static getFileName(): string {
        return t('Article List').replace(/[^a-z0-9]/gi, '_') + '.pdf';
    }

    private static showLibError(resolve: () => void, reject: () => void): void {
        DialogActions.showErrorMessage(
            t('The PDF file could not be generated.'),
            () => { PDFMaker.loadLib().then(() => { resolve(); }); },
            reject
        );
    }

    private static loadLib(): Promise<void> {
        if ((window as any).pdfMake != null) {
            return Promise.resolve();
        }

        // load fonts using jQuery
        const tPromise: Promise<void> = new Promise<void>((resolve, reject) => {
            jQuery.ajax(PDFMaker.AjaxBundleConfig).done(() => {
                resolve();
            }).fail((pJqXHR: JQueryXHR) => {
                PDFMaker.showLibError(resolve, reject);
            });
        });
        return (tPromise);
    }

    private static generatePDFDocument = async (): Promise<any> => {
        const tArticleList: Array<ArticleItem> = ArticleStore.getState().articleList;
        const tShowPrices: boolean = UIHelper.showPrices();
        let tContent: Array<any>;
        if (tShowPrices) {
            tContent = [
                [
                    {text: t('Pos.'), style: 'tableHead'},
                    {text: t('Article Image'), style: 'tableHead'},
                    {text: t('Description'), style: 'tableHead'},
                    {text: t('Quantity'), style: 'tableHead', alignment: 'right'},
                    {text: t('Single Price'), style: 'tableHead', alignment: 'right'},
                    {text: t('Total Price'), style: 'tableHead', alignment: 'right'}
                ]
            ];
        } else {
            tContent = [
                [
                    {text: t('Pos.'), style: 'tableHead'},
                    {text: t('Article Image'), style: 'tableHead'},
                    {text: t('Description'), style: 'tableHead'},
                    {text: t('Quantity'), style: 'tableHead', alignment: 'right'}
                ]
            ];
        }
        let tMainPos: number = 0;
        let tsubPos: number = 1;
        let tposStr: string = '';
        let totalPrice: number = 0;
        let dontBreakRows: boolean = true;
        for (const tArticle of tArticleList) {
            if (tArticle.properties.article == null || tArticle.properties.itemId == null) {
                continue;
            }

            const tmainId: egr.wcf.cf.MainArticleElement = tArticle.article.getMainArticle();
            if (tArticle.properties.itemId === tmainId.basketId) {
                tMainPos++;
                tsubPos = 0;
            } else {
                const tsubArt: Array<egr.wcf.cf.SubArticleElement> = tArticle.article.getSubArticles(true);
                if (tsubArt.length > 0) {
                    tMainPos++;
                    tsubPos = 0;
                } else {
                    tsubPos++;
                }
            }

            if (tsubPos > 0) {
                tposStr = tMainPos.toString() + '.' + tsubPos.toString();
            } else {
                tposStr = tMainPos.toString();
            }

            if (
                tArticle.properties.article.salesPrice != null &&
                utils.string.isNotNullOrEmpty(tArticle.properties.article.currency)
            ) {
                totalPrice += PriceStore.getPrice(tArticle.properties.article.salesPrice, tArticle.properties.article.currency);
            }
            let imageCell: any = {
                alignment: 'left',
                text: '',
                width: 48,
                height: 48,
                margin: [0, 5]
            };

            if (!egr.wcf.utils.string.isNullOrEmpty(tArticle.image)) {
                imageCell = {
                    alignment: 'left',
                    fit: [65, 65],
                    image: tArticle.image,
                    margin: [0, 5]
                };
            }
            let features: Array<string> = [];
            if (utils.string.isNotNullOrEmpty(tArticle.properties.article.featureText)) {
                features = UIHelper.multilineTextToArray(tArticle.properties.article.featureText);
            }
            let longText: Array<string> = [];
            if (utils.string.isNotNullOrEmpty(tArticle.properties.article.longText)) {
                longText = UIHelper.multilineTextToArray(tArticle.properties.article.longText);
            }
            if ((features.length + longText.length) > 15) {
                dontBreakRows = false;
            }

            if (tShowPrices && tArticle.properties.article != null) {
                tContent.push([
                    {text: tposStr, margin: [0, 5]},
                    imageCell,
                    {table: {
                        headerRows: 0,
                        widths: ['100%'],
                        body: [
                            [{
                                text:
                                    ArticleListMaker.getArticleHeader(tArticle.properties) +
                                    '\n' +
                                    ArticleListMaker.getManufacturerHeader(tArticle.properties) +
                                    '\n',
                                style: 'justbold'
                            }],
                            [{
                                text: tArticle.properties.article.longText
                            }],
                            [{
                                ul: features,
                            }]
                        ]
                        },
                        layout: 'noBorders',
                        margin: [0, 5]
                    },
                    {
                        text: '1', style: 'justbold', alignment: 'right',
                        margin: [0, 5]
                    },
                    {
                        text: ArticleListMaker.getPriceString(tArticle.properties, false),
                        alignment: 'right', style: 'justbold',
                        margin: [0, 5]
                    },
                    {
                        text: ArticleListMaker.getPriceString(tArticle.properties, true),
                        alignment: 'right', style: 'justbold',
                        margin: [0, 5]
                    }
                ]);
                tContent.push([
                    {text: '', margin: [0, -2.5]},
                    {text: '', margin: [0, -2.5]},
                    {text: '', margin: [0, -2.5]},
                    {text: '', margin: [0, -2.5]},
                    {text: '', margin: [0, -2.5]},
                    {text: '', margin: [0, -2.5]}
                ]);
            } else {
                tContent.push([
                    {text: tposStr, margin: [0, 5]},
                    imageCell,
                    {
                        table: {
                            headerRows: 0,
                            widths: ['100%'],
                            body: [
                                [{text: (
                                    ArticleListMaker.getArticleHeader(tArticle.properties) + '\n' +
                                    ArticleListMaker.getManufacturerHeader(tArticle.properties) + '\n'
                                ), style: 'justbold'}],
                                [{
                                    text: tArticle.properties.article.longText
                                }],
                                [{ul: features}]
                            ]
                        },
                        layout: 'noBorders',
                        margin: [0, 5]
                    },
                    {text: '1', style: 'justbold', alignment: 'right', margin: [0, 5]}
                ]);
                tContent.push([
                    {text: '', margin: [0, -2.5]},
                    {text: '', margin: [0, -2.5]},
                    {text: '', margin: [0, -2.5]},
                    {text: '', margin: [0, -2.5]}
                ]);
            }
        }
        if (tShowPrices && totalPrice !== 0) {
            tContent.push(
                [
                    '',
                    '',
                    {text: t('Price (excl. Tax)'), style: 'justbold'},
                    '',
                    '',
                    {text: PriceStore.getPriceString(totalPrice, ProjectSettingsStore.getState().currency), style: 'justbold', alignment: 'right'}
                ]
            );
        }
        let tWidths: Array<string>;
        if (tShowPrices) {
            tWidths = ['5.1%', '15%', '35%', '10%', '17%', '17%'];
        } else {
            tWidths = ['5.1%', '20%', '64%', '10%'];
        }
        /*
        margin: [left, top, right, bottom]
        margin: [horizontal, vertical]
        margin: equalLeftTopRightBottom
        */
        const tScreenshotWithPositionMarker: string = await ScreenshotHelper.getScreenshot({
            width: DownloadImageSize.width,
            height: DownloadImageSize.height,
            renderAsync: false,
            addWatermark: true,
            customMimetype: true,
            withPositionMarker: true,
        });

        const tScreenshot: string = await ScreenshotHelper.getScreenshot({
            width: DownloadImageSize.width,
            height: DownloadImageSize.height,
            renderAsync: false,
            addWatermark: true,
            customMimetype: true
        });
        const docDefinition: any = {
            pageSize: 'A4',
            pageMargins: [53, 95, 53, 50],
            header: (page: number, pages: number): any => {
                return {
                    margin: [53, 23, 53, 0],
                    table: {
                        headerRows: 0,
                        widths: ['*', 130],
                        body: [
                            [
                                '',
                                {
                                    alignment: 'left',
                                    // tslint:disable-next-line:max-line-length
                                    image: 'data:image/png;base64,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',
                                    width: 115,
                                    margin: [0, 5]
                                }
                            ]
                        ]
                    },
                    layout: 'noBorders'
                };
            },
            footer,
            content: [
                {
                    table: {
                        widths: ['*', 130],
                        body: [
                            [
                                '',
                                {
                                    stack: [
                                        {
                                            fontSize: 8,
                                            table: {
                                                body: [
                                                    [
                                                        {
                                                            stack: [
                                                                t('B.PRO GmbH'),
                                                            ]
                                                        }
                                                    ],
                                                    [
                                                        {
                                                            stack: [
                                                                t('Flehinger Strasse 59'),
                                                                t('75038 Oberderdingen\nGERMANY')
                                                            ]
                                                        }
                                                    ],
                                                    [
                                                        {
                                                            stack: [
                                                                t('P. O. Box 13 10'),
                                                                t('75038 Oberderdingen\nGERMANY')
                                                            ]
                                                        }
                                                    ],
                                                    [
                                                        t('www.bpro-solutions.com')
                                                    ],
                                                    [
                                                        (new Date(Date.now()).toLocaleDateString(GlobalHelper.coreApp.i18n.locale))
                                                    ]
                                                ]
                                            },
                                            layout: {
                                                hLineWidth: (i: number, node: any): number => {
                                                    return i === node.table.body.length || i === 0 ? 0 : 7;
                                                },
                                                hLineColor: (i: number, node: any): string => {
                                                    return 'white';
                                                },
                                                vLineWidth: (i: number, node: any): number => {
                                                    return i === node.table.widths.length || i === 0 ? 0 : 10;
                                                },
                                                vLineColor: (i: number, node: any): string => {
                                                    return 'white';
                                                },
                                                paddingTop: (i: number, node: any): number => {
                                                    return 0;
                                                },
                                                paddingBottom: (i: number, node: any): number => {
                                                    return 0;
                                                },
                                                paddingLeft: (i: number, node: any): number => {
                                                    return 0;
                                                },
                                                paddingRight: (i: number, node: any): number => {
                                                    return 0;
                                                }
                                            }
                                        },
                                    ]
                                }
                            ]
                        ]
                    },
                    layout: 'noBorders'
                },
                {
                    style: 'paragraph',
                    stack: [
                        {
                            text: t('Article List'),
                            style: 'headerheadline'
                        },
                        '\n',
                        '\n',
                        t('Dear Sir/Madam,'),
                        '\n',
                        t('Thank you for your interest in our products.')
                    ]
                },
                {
                    style: 'paragraph',
                    table: {
                        headerRows: 1,
                        widths: tWidths,
                        dontBreakRows: dontBreakRows,
                        body: tContent
                    },
                    layout: {
                        hLineWidth: (i: number, node: any): number => {
                            if (i === 0 || i === 1 || i === (tShowPrices ? (node.table.body.length - 2) : node.table.body.length)) {
                                return .5;
                            }
                            if (i === node.table.body.length) {
                                return 0;
                            }
                            return (i % 2 === 0 ? .25 : 0);
                        },
                        vLineWidth: (i: number, node: any): number => {
                            return 0;
                        },
                        hLineColor: (i: number, node: any): string => {
                            if (i === 0 || i === 1 || i === (tShowPrices ? (node.table.body.length - 2) : node.table.body.length)) {
                                return 'black';
                            }
                            if (i === node.table.body.length) {
                                return 'white';
                            }
                            return (i % 2 === 0 ? '#8c918c' : 'white');
                        },
                        vLineColor: (i: number, node: any): string => {
                                return 'black';
                        },
                        paddingTop: (i: number, node: any): number => {
                            if (i === node.table.body.length || i === 0) {
                                return 2;
                            }
                            return (i % 2 === 0 ? 0 : 2); // spacer
                        },
                        paddingBottom: (i: number, node: any): number => {
                            if (i === node.table.body.length || i === 0) {
                                return 2;
                            }
                            return (i % 2 === 0 ? 0 : 2); // spacer
                        }
                    }
                },
                {
                    style: 'paragraph',
                    stack: tShowPrices ?
                    [
                        // tslint:disable-next-line:max-line-length
                        {text: '*** ' + t('Please note the currently valid material cost surcharge') + ' ***', style: 'redfont'},
                        '\n',
                        '\n',
                        t('If you have any questions, our sales team will be glad to help.'),
                        t('Subject to technical changes.'),
                        '\n',
                        t('With best regards,'),
                        '\n',
                        t('The B.PRO team')
                    ] : [
                        t('If you have any questions, our sales team will be glad to help.'),
                        t('Subject to technical changes.'),
                        '\n',
                        t('With best regards,'),
                        '\n',
                        t('The B.PRO team')
                    ]
                },
                {
                    alignment: 'center',
                    fit: [485, 485],
                    image: tScreenshotWithPositionMarker,
                    margin: [0, 0, 2, 0],
                    pageBreak: 'before'
                },
                {
                    alignment: 'center',
                    fit: [485, 485],
                    image: tScreenshot,
                    margin: [0, 0, 2, 0],
                    pageBreak: 'before'
                }
            ],
            defaultStyle: {
                font: 'Arial',
                fontSize: 11,
                color: '#000000'
            },
            styles: {
                headerstyle: {
                    margin: [ 50, 50, 50, 50 ]
                },
                justbold: {
                    bold: true
                },
                headerheadline: {
                    fontSize: 14,
                    margin: [0, 50, 0, 0]
                },
                headerinfo: {
                    fontSize: 12
                },
                greyfont: {
                    color: '#8c918c'
                },
                link: {
                    color: '#0000EE',
                    decoration: 'underline'
                },
                redfont: {
                    bold: true,
                    color: '#FF3737',
                },
                tableHead: {
                    bold: true,
                    margin: [0, 7, 0, 7]
                },
                paragraph: {
                    margin: [0, 0, 0, (11 * 1.5)]
                }
            }
        };

        window.pdfMake.fonts = {
            Arial: {
                normal: 'arial.ttf',
                bold: 'arialbd.ttf',
                italics: 'ariali.ttf',
                bolditalics: 'arialbi.ttf'
            }
        };
        return window.pdfMake.createPdf(docDefinition);
    };
}

function footer(page: number, pages: number): any {
    return {
        stack: [
            // should not be translated
            { text: 'B.PRO GmbH | Sitz: Oberderdingen | Handelsregister Mannheim, HRB 741623 | Ust-IdNr.: DE 255114478' },
            { text: 'Geschäftsführer: Andreas Ostermann von Roth' },
            { text: '\n' },
            { text: page.toString() + '/' + pages.toString(), alignment: 'right' }
        ],
        alignment: 'center',
        fontSize: 8,
        margin: [53, 0, 53, 0]
    };
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/PDFMaker.ts